import React from "react";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Tooltip,
  Skeleton,
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { makeStyles, withStyles } from "@material-ui/styles";
import { SkeletonTableRow } from "src/components/Skeleton/skeleton.component";
import RefreshTable from "src/components/Table/refreshTable";

const useStyle = makeStyles((theme) => ({
  container: {
    padding: 16,
    background: "white",
    borderRadius: 4,
  },
  tableContainer: {
    maxHeight: 350,
    overflowY: "auto",
  },
  table: {
    border: "1px solid #D0D5DD",
    tableLayout: "auto",
    whiteSpace: "nowrap",
    maxHeight: 500,
    overflowY: "auto",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
    "& th": {
      backgroundColor: "white",
      color: "#98A2B3",
    },
  },
  greenLabel: {
    color: "#6AAF68",
  },
  emptyContainer: {
    backgroundColor: theme.palette.background.paper,
    alignItems: "center",
    display: "flex",
    placeContent: "center",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "4px",
  },
  filterSection: {
    display: "grid",
    gridTemplateColumns: "repeat(3,1fr)",
    columnGap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  filterBox: {
    height: 34,
    backgroundColor: "white",
    border: "2px solid #D0D5DD",
  },
  paperRoot: {
    width: "217px",
  },
  TotalQuantityCount: {
    padding: "16px",
    borderRadius: "12px",
  },
  TotalQuantityCountBox: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "10px",
  },
}));

const StyledTooltip = withStyles({
  tooltip: {
    backgroundColor: "#3A4D54",
    color: "white",
    padding: 16,
  },
})(Tooltip);

export default function SnAvailabilityListTableComponent({
  isLoading,
  error,
  list,
  handleReload,
  format,
  activeType,
  formik,
  selectedProduct,
}) {
  // Helper to determine if a row is skipped (same product)
  const isRowSkipped = (sn) => {
    if (!selectedProduct || !sn.product) return false;
    return sn.product.id === selectedProduct.product_uuid;
  };
  const classes = useStyle();

  const SERIAL_NUMBER_TYPE_NUMBER = "number";

  return (
    <Paper elevation={0} className={classes.container} id="preview-table">
      {!isLoading && error && <RefreshTable handleReload={handleReload} />}
      {(isLoading || !!list.length) && (
        <Box>
          <TableContainer className={classes.tableContainer}>
            <Table
              className={classes.table}
              aria-label="sn table"
              size="small"
              stickyHeader
            >
              <TableHead style={{ height: "45px", background: "white" }}>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2">No.</Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        <b>Serial No.</b>
                      </Typography>
                      <Typography variant="body2">From</Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={{ verticalAlign: "bottom" }}>
                    <Typography variant="body2">To</Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        <b>Sequence No.</b>
                      </Typography>
                      <Typography variant="body2">From</Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={{ verticalAlign: "bottom" }}>
                    <Typography variant="body2">To</Typography>
                  </TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>Batch Number</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading
                  ? [...new Array(5)].map((v, index) => (
                      <SkeletonTableRow key={index} columnCount={9} />
                    ))
                  : list.map((sn, index) => {
                      const pattern = format.split(",");

                      let fromSnDigit = sn.from_sn;
                      let toSnDigit = sn.to_sn;
                      if (pattern[0]) {
                        if (sn.from_sn.startsWith(pattern[0])) {
                          fromSnDigit = fromSnDigit
                            .slice(pattern[0].length)
                            .trim();
                        }
                        if (sn.to_sn.startsWith(pattern[0])) {
                          toSnDigit = toSnDigit.slice(pattern[0].length).trim();
                        }
                      }

                      if (pattern[2]) {
                        if (sn.from_sn.endsWith(pattern[2])) {
                          fromSnDigit = fromSnDigit
                            .slice(0, fromSnDigit.length - pattern[2].length)
                            .trim();
                        }
                        if (sn.to_sn.endsWith(pattern[2])) {
                          toSnDigit = toSnDigit
                            .slice(0, toSnDigit.length - pattern[2].length)
                            .trim();
                        }
                      }

                      return (
                        <TableRow
                          key={index}
                          hover
                          role="checkbox"
                          tabIndex={-1}
                        >
                          <TableCell>
                            <Typography color="textSecondary">
                              <b>{index + 1}</b>
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                variant="body2"
                                style={{ marginRight: 8 }}
                              >
                                <span className={classes.greenLabel}>
                                  <b>{pattern[0]}</b>
                                </span>
                                <span>
                                  <b>{fromSnDigit}</b>
                                </span>
                                <span className={classes.greenLabel}>
                                  <b>{pattern[2]}</b>
                                </span>
                              </Typography>
                              <Typography>
                                <b>-</b>
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              <span className={classes.greenLabel}>
                                <b>{pattern[0]}</b>
                              </span>
                              <span>
                                <b>{toSnDigit}</b>
                              </span>
                              <span className={classes.greenLabel}>
                                <b>{pattern[2]}</b>
                              </span>
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                variant="body2"
                                style={{ marginRight: 8 }}
                              >
                                <b>{sn.from_seq_num}</b>
                              </Typography>
                              <Typography>
                                <b>-</b>
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              <b>{sn.to_seq_num}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              color={
                                sn.quantity <= 0
                                  ? "textSecondary"
                                  : "textPrimary"
                              }
                              variant="body2"
                            >
                              <b>{sn.quantity}</b>
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {sn.batch_num}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {sn.product ? sn.product.name : "-"}
                          </TableCell>
                          <TableCell align="right">
                            {isRowSkipped(sn) ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                              >
                                <Typography
                                  variant="body2"
                                  style={{ color: "#98A2B3", marginRight: 4 }}
                                >
                                  Skipped
                                </Typography>
                                <StyledTooltip
                                  placement="top"
                                  title={
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        style={{ fontWeight: "bold" }}
                                      >
                                        Skipped
                                      </Typography>
                                      <Typography variant="body2">
                                        These serial numbers have been assigned
                                        to the {sn.product?.name || ""} product
                                      </Typography>
                                    </Box>
                                  }
                                >
                                  <HelpOutlineIcon
                                    fontSize="small"
                                    style={{ color: "#98A2B3", cursor: "help" }}
                                  />
                                </StyledTooltip>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                style={{ color: "#32D583" }}
                              >
                                Available
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box className={classes.TotalQuantityCountBox}>
            <Paper elevation={1} className={classes.TotalQuantityCount}>
              <Typography
                variant="body2"
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                Total Quantity :{" "}
                {isLoading ? (
                  <Skeleton variant="rectangular" width={80} height={10} />
                ) : (
                  <b>{formik.values.quantity}</b>
                )}
              </Typography>
            </Paper>
          </Box>
        </Box>
      )}
      {!isLoading && !error && !list.length && (
        <Box>
          <Table
            className={classes.table}
            aria-label="sn table"
            size="small"
            stickyHeader
          >
            <TableHead style={{ height: "45px" }}>
              <TableRow>
                <TableCell>No.</TableCell>
                {activeType === SERIAL_NUMBER_TYPE_NUMBER && (
                  <>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          <b>Serial No.</b>
                        </Typography>
                        <Typography variant="body2">From</Typography>
                      </Box>
                    </TableCell>
                    <TableCell style={{ verticalAlign: "bottom" }}>
                      <Typography variant="body2">To</Typography>
                    </TableCell>
                  </>
                )}
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      <b>Sequence No.</b>
                    </Typography>
                    <Typography variant="body2">From</Typography>
                  </Box>
                </TableCell>
                <TableCell style={{ verticalAlign: "bottom" }}>
                  <Typography variant="body2">To</Typography>
                </TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Batch Number</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
          </Table>
          <Box py={9} className={classes.emptyContainer}>
            <Typography
              variant="body1"
              style={{ paddingBottom: "16px", fontWeight: "bold" }}
            >
              No result. Please enter custom range
            </Typography>
          </Box>
          <Box className={classes.TotalQuantityCountBox}>
            <Paper elevation={1} className={classes.TotalQuantityCount}>
              <Typography variant="body2">Total Quantity :0</Typography>
            </Paper>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
